import * as React from 'react';
import { Zap } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  Callout,
  CheckRow,
  SegmentedControl,
  WorkspaceButton,
  WorkspaceField,
  WorkspaceInput,
  WorkspaceSelect,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import {
  useFollowUpDraftQuery,
  useRequestGapQuestion,
  useSendFollowUp,
  type FollowUpDraft,
} from '@/lib/api/discovery-findings';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import { plural } from '../discovery-logic';
import { followUpCountOk, followUpQuestions } from './findings-logic';

/**
 * Ask a follow-up about an area (E12-F07, rule 19, Decision 53.13), in
 * place under the area's status: someone who is not a source yet, 1 to 8
 * questions (the area's own, AI's gap question with its reason, AI's
 * question for a step of the process nobody has described (T-0286), or
 * your own), by secure link or added to their next interview. Opened from
 * such a step (`pickAi`), it starts with that step's question picked and
 * nothing else. Prototype: `inlineForm` (kind `followup`) in
 * `docs/discovery/prototype/discovery.js`.
 */

type Channel = 'link' | 'next_interview';
type Candidate = FollowUpDraft['candidates'][number];

/** Why this person cannot be asked this way, or null. */
function blocker(c: Candidate | undefined, channel: Channel): string | null {
  if (!c) return 'Pick who to ask';
  if (channel === 'link' && !c.email) {
    return `${c.full_name} has no email. Add one, or add the questions to their next interview`;
  }
  if (channel === 'next_interview' && c.next_interviews.length === 0) {
    return `${c.full_name} has no interview coming up. Send a secure link instead`;
  }
  return null;
}

const toggled = (set: ReadonlySet<string>, id: string) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

export function FollowUpForm({
  engagementId,
  areaId,
  areaLabel,
  pickAi = null,
  onDone,
}: {
  engagementId: string;
  areaId: string;
  areaLabel: string;
  /** An AI question to start with, picked: the one of the step the form was opened from. */
  pickAi?: string | null;
  onDone: () => void;
}) {
  // While AI drafts the gap question, ask for the draft again for a minute.
  const [drafting, setDrafting] = React.useState(false);
  React.useEffect(() => {
    if (!drafting) return;
    const t = setTimeout(() => setDrafting(false), 60_000);
    return () => clearTimeout(t);
  }, [drafting]);
  const draft = useFollowUpDraftQuery(engagementId, areaId, drafting);
  const send = useSendFollowUp(engagementId);
  const gap = useRequestGapQuestion(engagementId);
  const { toast } = useToast();
  const [personId, setPersonId] = React.useState('');
  const [framework, setFramework] = React.useState<Set<string>>(() => new Set());
  const [ai, setAi] = React.useState<Set<string>>(() => new Set());
  const [custom, setCustom] = React.useState('');
  const [channel, setChannel] = React.useState<Channel>('link');
  const [error, setError] = React.useState<string | null>(null);
  // The starting picks are made once, when the draft first arrives.
  const seeded = React.useRef(false);

  // Start from the first person who can be asked by link, and the area's first two questions.
  const d = draft.data;
  React.useEffect(() => {
    if (!d) return;
    const first = d.candidates.find((c) => c.email) ?? d.candidates[0];
    setPersonId((p) => p || first?.person_id || '');
    // The starting picks are made once. Opened from a step: its question
    // alone. Otherwise the area's first two.
    if (seeded.current) return;
    seeded.current = true;
    if (pickAi !== null && d.ai_questions.some((x) => x.ai_proposal_id === pickAi)) {
      setAi(new Set([pickAi]));
    } else {
      setFramework(new Set(d.questions.slice(0, 2).map((x) => x.question_id)));
    }
  }, [d, pickAi]);
  React.useEffect(() => {
    // The area's own question arrived (a step's question is not what was asked for).
    if (d?.ai_questions.some((x) => !x.step)) setDrafting(false);
  }, [d]);

  const cancel = (
    <WorkspaceButton variant="tertiary" size="md" onClick={onDone}>
      Cancel
    </WorkspaceButton>
  );
  const frame = (body: React.ReactNode, footer: React.ReactNode) => (
    <section aria-label="Ask a follow-up">
      <Callout tone="form">
        <h3 className="text-body-sm font-semibold text-ink-900">
          Ask a follow-up about {areaLabel}
        </h3>
        <p className="mb-2.5 text-meta text-ink-500">
          Ask someone who isn’t a source yet; their answers count as a new source.
        </p>
        <div className="flex flex-col gap-3">{body}</div>
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2.5">{footer}</div>
      </Callout>
    </section>
  );

  if (draft.error || !d) {
    return frame(
      draft.error ? (
        <ErrorMessage error={draft.error} />
      ) : (
        <span className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading who can be asked…
        </span>
      ),
      cancel,
    );
  }

  const max = d.max_questions;
  // The area's own question, and the questions of the steps nobody has described (T-0286).
  const areaAi = d.ai_questions.filter((x) => !x.step);
  const stepAi = d.ai_questions.filter((x) => x.step);
  const person = d.candidates.find((c) => c.person_id === personId);
  const nextInterview = person?.next_interviews[0] ?? null;
  const questions = followUpQuestions({ framework, ai, custom });
  const n = questions.length;
  const why =
    blocker(person, channel) ??
    (n === 0 ? 'Pick at least one question' : n > max ? `At most ${max} questions` : null);

  const submit = () => {
    if (why || !person || !followUpCountOk(n, max)) return;
    setError(null);
    send.mutate(
      {
        body: {
          area_id: areaId,
          person_id: person.person_id,
          questions,
          channel,
          ...(channel === 'next_interview' && nextInterview
            ? { interview_id: nextInterview.interview_id }
            : {}),
        },
        key: newIdempotencyKey(),
      },
      {
        onSuccess: () => {
          toast({
            message:
              channel === 'link'
                ? `Sent: ${plural(n, 'question')} to ${person.full_name} about ${areaLabel}. Their answers will count as a new source.`
                : `Added ${plural(n, 'question')} to ${nextInterview?.code ?? 'their next interview'} for ${person.full_name}. Their answers will count as a new source.`,
          });
          onDone();
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : 'Could not send the follow-up.'),
      },
    );
  };

  const draftGap = () =>
    gap.mutate(areaId, {
      onSuccess: () => setDrafting(true),
      onError: (e) =>
        toast({
          message: e instanceof ApiError ? e.message : 'Could not ask AI for a question.',
          tone: 'critical',
        }),
    });

  return frame(
    <>
      {d.candidates.length === 0 ? (
        <p className="text-body-sm text-ink-500">
          Everyone named in this engagement is already a source for {areaLabel}. Add a person in
          Sessions, or add evidence from a document.
        </p>
      ) : (
        <WorkspaceField label="Who">
          {(id) => (
            <WorkspaceSelect
              id={id}
              className="max-w-[420px]"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
            >
              {d.candidates.map((c) => (
                <option key={c.person_id} value={c.person_id}>
                  {c.title ? `${c.full_name}, ${c.title}` : c.full_name}
                </option>
              ))}
            </WorkspaceSelect>
          )}
        </WorkspaceField>
      )}
      <div>
        <p className="mb-1 text-meta font-medium text-ink-700">
          Questions (1 to {max}) <span className="font-normal text-ink-500">· {n} picked</span>
        </p>
        <div role="group" aria-label="Questions">
          {d.questions.map((x) => (
            <CheckRow
              key={x.question_id}
              checked={framework.has(x.question_id)}
              onToggle={() => setFramework((s) => toggled(s, x.question_id))}
            >
              {x.text}
            </CheckRow>
          ))}
        </div>
        <p className="mb-0.5 mt-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ai-ink">
          <Zap className="h-3 w-3" aria-hidden="true" /> Drafted by AI for this gap
        </p>
        {areaAi.length ? (
          <div role="group" aria-label="Drafted by AI for this gap">
            {areaAi.map((x) => (
              <CheckRow
                key={x.ai_proposal_id}
                checked={ai.has(x.ai_proposal_id)}
                onToggle={() => setAi((s) => toggled(s, x.ai_proposal_id))}
              >
                {x.text}
                <span className="mt-0.5 block text-meta text-ai-ink">{x.why}</span>
              </CheckRow>
            ))}
          </div>
        ) : (
          <p className="flex flex-wrap items-center gap-2 py-1.5 text-meta text-ink-500">
            {drafting || gap.isPending ? (
              <>
                <Spinner /> AI is drafting a question aimed at this gap…
              </>
            ) : (
              <>
                No question drafted for this gap yet.
                <button type="button" className={linkishClass()} onClick={draftGap}>
                  Draft one now
                </button>
              </>
            )}
          </p>
        )}
        {stepAi.length ? (
          <>
            <p className="mb-0.5 mt-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ai-ink">
              <Zap className="h-3 w-3" aria-hidden="true" /> Drafted by AI for steps nobody has
              described
            </p>
            <div role="group" aria-label="Drafted by AI for steps nobody has described">
              {stepAi.map((x) => (
                <CheckRow
                  key={x.ai_proposal_id}
                  checked={ai.has(x.ai_proposal_id)}
                  onToggle={() => setAi((s) => toggled(s, x.ai_proposal_id))}
                >
                  {x.text}
                  <span className="mt-0.5 block text-meta text-ink-500">
                    About the step: {x.step?.name}
                  </span>
                  <span className="mt-0.5 block text-meta text-ai-ink">{x.why}</span>
                </CheckRow>
              ))}
            </div>
          </>
        ) : null}
        <WorkspaceInput
          aria-label="Add your own question"
          placeholder="Add your own question"
          className="mt-1.5"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
        />
      </div>
      {error ? (
        <p role="alert" className="text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </>,
    <>
      <SegmentedControl
        aria-label="How to ask"
        value={channel}
        onChange={setChannel}
        options={[
          { value: 'link', label: 'Send by secure link' },
          { value: 'next_interview', label: 'Add to their next interview' },
        ]}
      />
      <span className="flex-1" />
      {why ? <span className="text-meta text-ink-500">{why}.</span> : null}
      {cancel}
      <WorkspaceButton
        variant="primary"
        size="md"
        disabled={Boolean(why) || send.isPending}
        onClick={submit}
      >
        {send.isPending
          ? 'Sending…'
          : channel === 'link'
            ? `Send ${plural(n, 'question')}`
            : `Add to ${nextInterview?.code ?? 'interview'}`}
      </WorkspaceButton>
    </>,
  );
}
